/**
 * API Client for Pulse
 * Provides type-safe API calls with error handling and automatic token refresh
 */

import type { ValidationErrorDetails } from './types';

// API base URL:
// - local dev keeps same-origin so Vite proxy can handle /api
// - production defaults to the dedicated API domain unless explicitly overridden
const API_BASE_URL = import.meta.env.VITE_API_URL
  || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? ''
    : 'https://api.youvebeenheard.com');

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: ValidationErrorDetails;
  };
}

type AuthFailureListener = () => void;

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: ValidationErrorDetails
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Track consecutive network errors to prevent infinite loops
let consecutiveNetworkErrors = 0;
const MAX_NETWORK_ERRORS = 3;
const authFailureListeners = new Set<AuthFailureListener>();

function notifyAuthFailure(): void {
  for (const listener of authFailureListeners) {
    listener();
  }
}

export function subscribeToAuthFailures(listener: AuthFailureListener): () => void {
  authFailureListeners.add(listener);
  return () => {
    authFailureListeners.delete(listener);
  };
}

/**
 * Check if an error is a network error (connection lost, etc.)
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    // Network errors from fetch are TypeErrors
    return true;
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return msg.includes('network') || msg.includes('failed to fetch') || msg.includes('load failed');
  }
  return false;
}

/**
 * Attempt to refresh the access token using the refresh token
 * Returns true if refresh succeeded, false otherwise
 */
async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: ApiResponse = await response.json();
        return data.success === true;
      }
      return false;
    } catch (error) {
      // Log network errors for debugging but don't crash
      if (isNetworkError(error)) {
        console.warn('[API] Token refresh failed due to network error');
      }
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const isRetry = retryCount > 0;
  const shouldAttemptRefreshOn401 = endpoint === '/api/auth/me' || !endpoint.startsWith('/api/auth/');

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    // Reset network error counter on successful fetch
    consecutiveNetworkErrors = 0;
  } catch (error) {
    // Handle network errors with retry logic
    if (isNetworkError(error)) {
      consecutiveNetworkErrors++;

      // If we've hit too many consecutive errors, throw to prevent loop
      if (consecutiveNetworkErrors >= MAX_NETWORK_ERRORS) {
        consecutiveNetworkErrors = 0;
        throw new ApiError(
          'NETWORK_ERROR',
          'Unable to connect to the server. Please check your internet connection.',
          0
        );
      }

      // Wait briefly and retry for network errors (not for retries to prevent infinite loop)
      if (retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return request<T>(endpoint, options, retryCount + 1);
      }
    }

    throw new ApiError(
      'NETWORK_ERROR',
      'Unable to connect to the server. Please check your internet connection.',
      0
    );
  }

  // Handle 401 - try to refresh token (but not for auth endpoints or retries)
  if (
    response.status === 401 &&
    !isRetry &&
    shouldAttemptRefreshOn401
  ) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry the original request
      return request<T>(endpoint, options, 1);
    }
    notifyAuthFailure();
    // Refresh failed - redirect to login (but only if not already on login page)
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
    throw new ApiError('SESSION_EXPIRED', 'Your session has expired. Please log in again.', 401);
  }

  const data: ApiResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    throw new ApiError(
      data.error?.code || 'UNKNOWN_ERROR',
      data.error?.message || 'An unknown error occurred',
      response.status,
      data.error?.details
    );
  }

  return data.data as T;
}

export function parseValidationError<TField extends string>(
  error: unknown,
  fields: readonly TField[],
  expectedCode = 'VALIDATION_FAILED'
): { fieldErrors: Partial<Record<TField, string>>; message: string } | null {
  if (!(error instanceof ApiError) || error.code !== expectedCode || !error.details) {
    return null;
  }

  const allowed = new Set<TField>(fields);
  const fieldErrors: Partial<Record<TField, string>> = {};

  for (const [key, value] of Object.entries(error.details)) {
    if (allowed.has(key as TField)) {
      fieldErrors[key as TField] = value;
    }
  }

  return {
    fieldErrors,
    message: error.message,
  };
}

export const api = {
  get: <T>(endpoint: string): Promise<T> => request<T>(endpoint, { method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown): Promise<T> =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown): Promise<T> =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown): Promise<T> =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, body?: unknown): Promise<T> =>
    request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),
};

export { ApiError };
